import { registerFigure, registerOverlay } from 'klinecharts'
import type { OverlayCreateFiguresCallbackParams, OverlayFigureIgnoreEventType } from 'klinecharts'
import { layoutBarCountdownPill, nicePillY } from './barCountdownPill'
import { CHART_FONT_FAMILY } from './chartColors'
import { hexToRgba } from './color'
import { LUCIDE_BELL_PATHS } from './iconChipFigure'
import { type OverlayFigure } from './overlayUtils'
import { buildReferencePriceFigures } from './referencePriceLine'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'
import { inkOn } from './tpoSettings'

/**
 * Price alert lines (chart crosshair alert spec, part C): each of the user's active alerts
 * for the chart's ticker as a dashed line with a bell label on the price axis. Data lines,
 * not drawings: ChartManager.setPriceAlertLines creates them locked and without the drawing
 * callbacks, so they are never persisted, synced or selectable as drawings, and the
 * drawing-bar eye leaves them alone (DATA_LINE_OVERLAY_NAMES).
 *
 * Cancel (C3) is a right click or a double click on the line or its label:
 * - right click (and a touch long press where the browser raises `contextmenu`): EdgeChart's
 *   contextmenu asks ChartManager.cancelPriceAlertAt, a geometric hit test (alertHitAt);
 * - double click or double tap on the line, or on a pane label: the transparent
 *   PRICE_ALERT_HIT_KEY figures below, which take nothing but double clicks;
 * - double click on the axis label: ChartManager's dblclick handler, because klinecharts
 *   never hands y-axis double clicks to figures (KC:12925-12934).
 */

export const PRICE_ALERT_LINE_NAME = 'priceAlertLine'
export const PRICE_ALERT_LABEL_FIGURE = 'priceAlertLabel'
/** Transparent figures that take a double click in the main pane. */
export const PRICE_ALERT_HIT_KEY = 'price_alert_hit'
/** Paint order only: over the passive axis pills (Today, Pre/Post, dilution: zLevel 0),
 *  under the countdown pill (BAR_COUNTDOWN_Z_LEVEL 1000). The crosshair label paints after
 *  every overlay (KC:10418-10421). */
export const PRICE_ALERT_Z_LEVEL = 500
/** Violet (spec C7): the default drawing colour is yellow #eab308
 *  (overlayUserDefaults.ts:53), so amber would read as one of the user's own lines. */
export const PRICE_ALERT_LINE_COLOR = '#a855f7'
/** A cancel in flight draws the line and the label at this alpha. */
export const CANCELLING_ALPHA = 0.4
/** A point this close to the line, in px, is on it: klinecharts' own line tolerance
 *  (DEVIATION, KC:4885; checkCoordinateOnLine, KC:4940-4965). */
export const PRICE_ALERT_HIT_PX = 5

/** Label metrics: 15px tall like the Today pill and the crosshair label. */
export const ALERT_LABEL = {
  padX: 4,
  textSize: 11,
  icon: 9,
  gap: 3,
  radius: 2,
  height: 15,
} as const

/** What an alert label needs beyond the crosshair label the axis tiers fit: bell, gap and
 *  padding (4 + 9 + 3 + 4) against the crosshair label's 4 + 4 padding (themes/dark.ts:133-134). */
export const ALERT_LABEL_AXIS_EXTRA = 12

/** Everything but a double click falls through: no press (panning and the axis drag-scale
 *  still work, KC:12826-12852), no click (a left click is a click on the chart), no right
 *  click (klinecharts' figure right-click would remove the overlay, KC:8805-8815). */
const DOUBLE_CLICK_ONLY: OverlayFigureIgnoreEventType[] = [
  'mouseClickEvent',
  'tapEvent',
  'mouseRightClickEvent',
  'mouseDownEvent',
  'touchStartEvent',
  'mouseMoveEvent',
  'touchMoveEvent',
]

const CLEAR = 'rgba(0,0,0,0)'

/** One alert line as the host hands it to EdgeChart. */
export interface PriceAlertLine {
  id: string
  level: number
  /** A cancel is in flight: drawn dimmed, no clicks. */
  cancelling?: boolean
}

/** The extendData ChartManager writes on each alert overlay. */
export interface PriceAlertLineExtend {
  alertId: string
  cancelling: boolean
  /** The host wired a cancel; without one nothing on the alert takes clicks. */
  cancellable: boolean
  /** Scales hidden: the label draws in the main pane at its edge, since the strip can be
   *  0 px wide (KC:10782-10849). */
  collapsed: boolean
  /** The countdown pill's close, read at paint time; null while the pill is off. */
  countdownClose: () => number | null
}

export function layoutPriceAlertLabel(input: {
  x: number
  y: number
  fromZero: boolean
  textWidth: number
}) {
  const { padX, icon, gap, height } = ALERT_LABEL
  const width = padX * 2 + icon + gap + Math.ceil(input.textWidth)
  const top = Math.round(input.y - height / 2)
  const left = input.fromZero ? input.x : input.x - width
  // The bell sits on the inner edge, beside the pane.
  const iconLeft = input.fromZero ? left + padX : left + width - padX - icon
  return {
    left,
    top,
    width,
    height,
    iconLeft,
    iconTop: top + (height - icon) / 2,
    textX: input.fromZero ? iconLeft + icon + gap : iconLeft - gap,
    textY: top + height / 2,
    textAlign: (input.fromZero ? 'left' : 'right') as CanvasTextAlign,
  }
}

/** A pane-layer label has no axis strip to span; this only bounds its targets. */
export function estimateLabelWidth(text: string): number {
  return ALERT_LABEL.padX * 2 + ALERT_LABEL.icon + ALERT_LABEL.gap + Math.ceil(text.length * 6.5)
}

/** The countdown pill's vertical extent for a raw y, placed as barCountdownPill.ts places it. */
export function countdownPillBand(rawY: number, paneHeight: number) {
  const l = layoutBarCountdownPill({
    x: 0,
    y: nicePillY(rawY, paneHeight),
    fromZero: true,
    priceWidth: 0,
    countdownWidth: 0,
  })
  return { top: l.top, bottom: l.top + l.height }
}

/** The alert's label row overlaps the countdown pill: it draws, but nothing on it takes
 *  clicks. Our own check, not z-order: the pill's figure ignores events
 *  (barCountdownPill.ts:108), so a click there would otherwise reach the hidden label. */
export function isCoveredByPill(y: number, band: { top: number; bottom: number } | null): boolean {
  if (!band) return false
  const top = Math.round(y - ALERT_LABEL.height / 2)
  return top < band.bottom && top + ALERT_LABEL.height > band.top
}

/**
 * The alert under a pointer, for a right click or an axis double click: the nearest line
 * within PRICE_ALERT_HIT_PX, or within the label's half height when the pointer is on its
 * label. Cancelling lines and lines under the countdown pill are never hit.
 */
export function alertHitAt(input: {
  /** Pointer y in candle-pane pixels. */
  y: number
  lines: Array<{ id: string; y: number; onLabel: boolean; cancelling?: boolean }>
  pillBand: { top: number; bottom: number } | null
}): string | null {
  let best: { id: string; d: number } | null = null
  for (const l of input.lines) {
    if (l.cancelling || !Number.isFinite(l.y) || isCoveredByPill(l.y, input.pillBand)) continue
    const reach = l.onLabel ? ALERT_LABEL.height / 2 : PRICE_ALERT_HIT_PX
    const d = Math.abs(input.y - l.y)
    if (d <= reach && (!best || d < best.d)) best = { id: l.id, d }
  }
  return best?.id ?? null
}

export function buildPriceAlertFigures(input: {
  ext: PriceAlertLineExtend
  layer: 'axis' | 'pane'
  y: number
  value: number
  pricePrecision: number
  boundingWidth: number
  fromZero: boolean
  /** The countdown pill's extent on this pane, or null. */
  pillBand: { top: number; bottom: number } | null
}): OverlayFigure[] {
  const { ext, layer } = input
  if (!Number.isFinite(input.y)) return []
  const takesClicks =
    ext.cancellable && !ext.cancelling && !isCoveredByPill(input.y, input.pillBand)
  const figures: OverlayFigure[] = []
  if (layer === 'pane') {
    const color = ext.cancelling
      ? hexToRgba(PRICE_ALERT_LINE_COLOR, CANCELLING_ALPHA)
      : PRICE_ALERT_LINE_COLOR
    figures.push(
      ...buildReferencePriceFigures({
        y: input.y,
        width: input.boundingWidth,
        value: input.value,
        pricePrecision: input.pricePrecision,
        color,
      }),
    )
    // klinecharts' line figure hit-tests within DEVIATION (KC:4940-4965), the same band
    // alertHitAt uses for a right click.
    if (takesClicks) {
      figures.push({
        key: PRICE_ALERT_HIT_KEY,
        type: 'line',
        ignoreEvent: DOUBLE_CLICK_ONLY,
        attrs: {
          coordinates: [
            { x: 0, y: input.y },
            { x: input.boundingWidth, y: input.y },
          ],
        },
        styles: { style: 'solid', size: 1, color: CLEAR },
      })
    }
  }
  // The label: on the axis, or in the pane at its edge while the scales are hidden
  // (as barCountdownPill.ts:102-104).
  if (ext.collapsed !== (layer === 'pane')) return figures
  const x = input.fromZero ? 0 : input.boundingWidth
  const text = input.value.toFixed(input.pricePrecision)
  figures.push({
    type: PRICE_ALERT_LABEL_FIGURE,
    ignoreEvent: true,
    attrs: { x, y: input.y, fromZero: input.fromZero, text },
    styles: {
      backgroundColor: PRICE_ALERT_LINE_COLOR,
      color: inkOn(PRICE_ALERT_LINE_COLOR),
      family: CHART_FONT_FAMILY,
      alpha: ext.cancelling ? CANCELLING_ALPHA : 1,
    },
  })
  // Only a pane label can take a klinecharts double click (KC:12920-12924); an axis
  // label's goes through ChartManager's dblclick handler.
  if (layer === 'pane' && takesClicks) {
    const w = estimateLabelWidth(text)
    figures.push({
      key: PRICE_ALERT_HIT_KEY,
      type: 'rect',
      ignoreEvent: DOUBLE_CLICK_ONLY,
      attrs: {
        x: input.fromZero ? x : x - w,
        y: Math.round(input.y - ALERT_LABEL.height / 2),
        width: w,
        height: ALERT_LABEL.height,
      },
      styles: { style: 'fill', color: CLEAR },
    })
  }
  return figures
}

interface LabelAttrs {
  x: number
  y: number
  fromZero: boolean
  text: string
}

interface LabelStyles {
  backgroundColor?: string
  color?: string
  family?: string
  alpha?: number
}

/** Box, bell and price in one figure (precedent: barCountdownPill.ts:137-183). */
const registerPriceAlertLabelFigure = registerOnce(() => {
  registerFigure({
    name: PRICE_ALERT_LABEL_FIGURE,
    checkEventOn: () => false,
    draw: (ctx: CanvasRenderingContext2D, attrs: unknown, styles: unknown) => {
      const a = attrs as LabelAttrs
      const s = (styles ?? {}) as LabelStyles
      const ink = s.color ?? inkOn(PRICE_ALERT_LINE_COLOR)
      ctx.save()
      ctx.globalAlpha = s.alpha ?? 1
      ctx.font = `normal ${ALERT_LABEL.textSize}px ${s.family ?? CHART_FONT_FAMILY}`
      const l = layoutPriceAlertLabel({
        x: a.x,
        y: a.y,
        fromZero: a.fromZero,
        textWidth: ctx.measureText(a.text).width,
      })
      ctx.beginPath()
      const roundRect = (
        ctx as CanvasRenderingContext2D & {
          roundRect?: (x: number, y: number, w: number, h: number, r: number) => void
        }
      ).roundRect
      if (roundRect) roundRect.call(ctx, l.left, l.top, l.width, l.height, ALERT_LABEL.radius)
      else ctx.rect(l.left, l.top, l.width, l.height)
      ctx.closePath()
      ctx.fillStyle = s.backgroundColor ?? PRICE_ALERT_LINE_COLOR
      ctx.fill()
      // Lucide's 24-unit bell scaled into the icon slot (iconChipFigure.ts:103-113).
      ctx.save()
      ctx.translate(l.iconLeft, l.iconTop)
      const k = ALERT_LABEL.icon / 24
      ctx.scale(k, k)
      ctx.strokeStyle = ink
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      for (const d of LUCIDE_BELL_PATHS) ctx.stroke(new Path2D(d))
      ctx.restore()
      ctx.fillStyle = ink
      ctx.textAlign = l.textAlign
      ctx.textBaseline = 'middle'
      ctx.fillText(a.text, l.textX, l.textY)
      ctx.restore()
    },
  } as Parameters<typeof registerFigure>[0])
})

const figuresFor =
  (layer: 'axis' | 'pane') =>
  ({
    overlay,
    coordinates,
    bounding,
    precision,
    yAxis,
  }: OverlayCreateFiguresCallbackParams): OverlayFigure[] => {
    const c = coordinates[0]
    const value = overlay.points[0]?.value
    const ext = overlay.extendData as PriceAlertLineExtend | undefined
    if (!c || value == null || !ext || !yAxis) return []
    const close = ext.countdownClose()
    return buildPriceAlertFigures({
      ext,
      layer,
      y: c.y,
      value,
      pricePrecision: precision.price,
      boundingWidth: bounding.width,
      fromZero: yAxis.isFromZero(),
      pillBand:
        close == null ? null : countdownPillBand(yAxis.convertToPixel(close), bounding.height),
    })
  }

export const registerPriceAlertLineOverlay = registerOnce(() => {
  registerPriceAlertLabelFigure()
  registerOverlay({
    name: PRICE_ALERT_LINE_NAME,
    totalStep: 2,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: figuresFor('pane'),
    createYAxisFigures: figuresFor('axis'),
    // Guard: no alert figure takes right clicks, but klinecharts' default figure
    // right-click removes the overlay unless this returns true (KC:8805-8815).
    onRightClick: () => true,
  } as OverlayTemplateParam)
})
